import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import AdminPortalLayout from './AdminPortalLayout';
import {
  AdminMe, fetchUserDetail, banUser, unbanUser, setUserBadge, setUserModerator,
  blockUser, unblockUser, sendUserPasswordReset, resendUserConfirmation,
} from '../lib/admin-portal-api';
import { FaArrowLeft, FaCheckCircle, FaBan, FaStar, FaExclamationTriangle, FaUserPlus } from '../lib/icons';
import { PlatformBadge, StatusPill } from './SubscriptionBadges';

interface Props { me: AdminMe }

const badges = ['member', 'moderator', 'expert', 'ambassador', 'admin'];

export default function AdminPortalUserDetail({ me }: Props) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    fetchUserDetail(Number(id))
      .then(setUser)
      .catch((e) => setError(e?.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [id]);

  const reload = async () => {
    if (!id) return;
    setUser(await fetchUserDetail(Number(id)));
  };

  const guard = async (fn: () => Promise<any>, okText: string | ((result: any) => string)) => {
    setBusy(true); setMsg(null);
    try {
      const result = await fn();
      setMsg({ type: 'success', text: typeof okText === 'function' ? okText(result) : okText });
      await reload();
    }
    catch (e: any) { setMsg({ type: 'error', text: e?.response?.data?.error || 'Action failed' }); }
    finally { setBusy(false); }
  };

  const onBan = () => {
    const reason = window.prompt('Reason for ban:');
    if (reason === null) return;
    const daysStr = window.prompt('Days (blank = permanent):');
    if (daysStr === null) return;
    const days = daysStr.trim() === '' ? undefined : Number(daysStr);
    guard(() => banUser(user.id, reason || '', days), 'User banned.');
  };
  const onUnban = () => guard(() => unbanUser(user.id), 'User unbanned.');
  const onBadgeChange = (b: string) => guard(() => setUserBadge(user.id, b), `Badge → ${b}`);
  const onModToggle = () => guard(() => setUserModerator(user.id, !user.is_community_moderator),
    user.is_community_moderator ? 'Moderator removed' : 'Moderator granted');

  const onBlockToggle = () => {
    if (user.blocked) return guard(() => unblockUser(user.id), 'Account unblocked.');
    if (!window.confirm('Block this user from logging in?')) return;
    guard(() => blockUser(user.id), 'Account blocked.');
  };

  const onSendPasswordReset = () => {
    if (!window.confirm(`Send a password reset link to ${user.email}?`)) return;
    guard(() => sendUserPasswordReset(user.id), (res) => res?.influencerResetSent
      ? 'Password reset emails sent (app account + influencer portal).'
      : 'Password reset email sent.');
  };

  const onResendConfirmation = () => {
    if (!window.confirm(`Resend confirmation email to ${user.email}?`)) return;
    guard(() => resendUserConfirmation(user.id), 'Confirmation email sent.');
  };

  return (
    <AdminPortalLayout me={me} active="users">
      <Helmet><title>{user?.email || 'User'} — EatMeat Admin</title></Helmet>

      <button onClick={() => navigate('/admin-portal/users')}
        className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 mb-4">
        <FaArrowLeft /> Back to users
      </button>

      {loading ? (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center text-sm text-gray-400">Loading…</div>
      ) : error ? (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>
      ) : user ? (
        <div className="grid lg:grid-cols-3 gap-4">
          {/* Identity */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary"><FaUserPlus /></div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-bold text-gray-800 text-lg truncate">{user.username || '—'}</h2>
                  <p className="text-sm text-gray-500 truncate">{user.email}</p>
                  <div className="flex gap-2 mt-2 flex-wrap text-[11px]">
                    {user.confirmed ? <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><FaCheckCircle /> Email confirmed</span>
                      : <span className="text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">Pending email</span>}
                    {user.blocked && <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 px-2 py-0.5 rounded-full"><FaBan /> Blocked</span>}
                    {user.is_premium && <span className="inline-flex items-center gap-1 text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full"><FaStar /> Premium</span>}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">Joined {new Date(user.createdAt).toLocaleString()} · ID {user.id}</p>
                </div>
              </div>
            </div>

            {msg && (
              <div className={`px-4 py-3 rounded-xl text-sm flex items-start gap-2 ${
                msg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800'
                                       : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {msg.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
                {msg.text}
              </div>
            )}

            {/* Subscription */}
            <SubscriptionCard sub={user.subscription} />


            {/* Referral events */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">
                Referral
                {user.referred_by && (
                  <span className="ml-2 text-xs font-normal text-gray-500">via <code className="bg-gray-100 px-1 rounded">{user.referred_by.code}</code> ({user.referred_by.name})</span>
                )}
              </h3>
              {user.referral_events?.length > 0 ? (
                <div className="space-y-2">
                  {user.referral_events.map((e: any) => (
                    <div key={e.id} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg">
                      <div>
                        <div className="text-xs font-semibold text-gray-700 capitalize">
                          {e.is_trial ? 'Trial' : e.is_first_paid ? 'Paid (first)' : e.event_type}
                          {e.referral && <span className="ml-2 text-gray-400">{e.referral.code}</span>}
                        </div>
                        <div className="text-[10px] text-gray-400">{new Date(e.createdAt).toLocaleString()} · {e.provider_store || '—'}</div>
                      </div>
                      <div className="text-right text-xs">
                        {e.amount ? <div className="font-semibold text-gray-700">{e.currency} {Number(e.amount).toFixed(2)}</div> : null}
                        {e.commission_amount ? <div className="text-green-600">+{e.currency} {Number(e.commission_amount).toFixed(2)} commission</div> : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">No referral events.</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {/* Account actions */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Account</h3>
              <div className="space-y-2">
                <button
                  onClick={onSendPasswordReset}
                  disabled={busy}
                  className="w-full py-2.5 rounded-xl text-sm font-semibold bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition-colors"
                >
                  Send password reset
                </button>

                {!user.confirmed && (
                  <button
                    onClick={onResendConfirmation}
                    disabled={busy}
                    className="w-full py-2.5 rounded-xl text-sm font-semibold bg-amber-50 text-amber-800 hover:bg-amber-100 disabled:opacity-50 transition-colors"
                  >
                    Resend confirmation email
                  </button>
                )}

                <button
                  onClick={onBlockToggle}
                  disabled={busy}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${
                    user.blocked
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-red-50 text-red-700 hover:bg-red-100'
                  }`}
                >
                  {user.blocked ? 'Unblock login' : 'Block login'}
                </button>
                <p className="text-[11px] text-gray-400">
                  Block prevents <em>any</em> login. Use the Community ban below if you only want to silence community posts.
                </p>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-800 mb-3">Community</h3>
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">Badge</div>
                  <select
                    value={user.community_badge || 'member'}
                    onChange={(e) => onBadgeChange(e.target.value)}
                    disabled={busy}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-primary focus:outline-none">
                    {badges.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <button
                  onClick={onModToggle}
                  disabled={busy}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                    user.is_community_moderator
                      ? 'bg-red-50 text-red-700 hover:bg-red-100'
                      : 'bg-green-50 text-green-700 hover:bg-green-100'
                  } disabled:opacity-50`}
                >
                  {user.is_community_moderator ? 'Remove moderator' : 'Grant moderator'}
                </button>

                <div className="pt-3 border-t border-gray-100">
                  {user.is_community_banned ? (
                    <div>
                      <div className="px-3 py-2 rounded-lg bg-red-50 text-xs text-red-700 mb-2">
                        Banned{user.community_ban_reason ? ` · ${user.community_ban_reason}` : ''}
                        {user.community_ban_until ? ` · until ${new Date(user.community_ban_until).toLocaleDateString()}` : ''}
                      </div>
                      <button onClick={onUnban} disabled={busy}
                        className="w-full py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:bg-primary/90 disabled:opacity-50">
                        Unban
                      </button>
                    </div>
                  ) : (
                    <button onClick={onBan} disabled={busy}
                      className="w-full py-2.5 rounded-xl text-sm font-semibold bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50">
                      Ban from community
                    </button>
                  )}
                </div>
              </div>
            </div>

            <Link to={`${process.env.REACT_APP_STRAPI_API_URL || ''}/admin/content-manager/collectionType/plugin::users-permissions.user/${user.id}`}
              target="_blank"
              className="block bg-white rounded-2xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-shadow">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Advanced</div>
              <div className="text-sm font-semibold text-primary mt-1">Open in Strapi admin →</div>
              <p className="text-[11px] text-gray-400 mt-1">For everything not exposed here.</p>
            </Link>
          </div>
        </div>
      ) : null}
    </AdminPortalLayout>
  );
}

/* ───── Subscription card ───── */

const DURATION_LABEL: Record<string, string> = {
  monthly: 'mo',
  quarterly: '3 mo',
  'half-yearly': '6 mo',
  yearly: 'yr',
};

function formatRelative(iso?: string | null): { text: string; tone: 'good' | 'warn' | 'bad' | 'mute' } | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  const diffMs = t - Date.now();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const abs = Math.abs(diffDays);
  if (diffDays > 0) {
    const tone = diffDays <= 3 ? 'warn' : 'good';
    return { text: diffDays === 1 ? 'Renews tomorrow' : `Renews in ${diffDays} days`, tone };
  }
  if (diffDays === 0) return { text: 'Renews today', tone: 'warn' };
  return { text: abs === 1 ? 'Expired 1 day ago' : `Expired ${abs} days ago`, tone: 'bad' };
}

function formatPrice(amount?: number | null, currency?: string | null) {
  if (!Number.isFinite(amount as number) || amount == null) return null;
  const value = (amount as number) / 100;
  const code = (currency || 'USD').toUpperCase();
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(value);
  } catch {
    return `${code} ${value.toFixed(2)}`;
  }
}

function SubscriptionCard({ sub }: { sub: any }) {
  if (!sub) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <h3 className="font-bold text-gray-800 mb-3">Subscription</h3>
        <div className="text-center py-6">
          <FaStar className="text-3xl mx-auto mb-2 text-gray-200" />
          <p className="text-sm text-gray-500">User has never subscribed.</p>
          <p className="text-[11px] text-gray-400 mt-1">No <code>user-subscription</code> record on file.</p>
        </div>
      </div>
    );
  }

  const status = sub.subscription_status as string | null;
  const relative = formatRelative(sub.current_period_end);
  const price = formatPrice(sub.plan?.amount, sub.plan?.currency);
  const cadence = sub.plan?.duration ? DURATION_LABEL[sub.plan.duration] || sub.plan.duration : null;
  const isActive = status === 'active';
  const cancelAtEnd = sub.cancel_at_period_end;

  const relTint = relative?.tone === 'good' ? 'bg-green-50 text-green-700'
    : relative?.tone === 'warn' ? 'bg-amber-50 text-amber-800'
    : relative?.tone === 'bad' ? 'bg-red-50 text-red-700'
    : 'bg-gray-100 text-gray-700';

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Hero strip with status accent */}
      <div className={`px-5 py-4 ${isActive ? 'bg-gradient-to-r from-green-50 to-emerald-50/40' : 'bg-gray-50'}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Subscription</div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-lg font-extrabold text-gray-900">{sub.plan?.name || 'Unknown plan'}</span>
              {price && (
                <span className="text-sm text-gray-600 font-semibold">
                  {price}{cadence ? ` / ${cadence}` : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 mt-2 items-center">
              <StatusPill status={status} />
              <PlatformBadge value={sub.platform_origin} />
              {relative && (
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${relTint}`}>
                  {relative.text}
                </span>
              )}
              {cancelAtEnd && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">
                  Cancels at period end
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Detail rows */}
      <div className="px-5 py-4 grid grid-cols-2 gap-3 text-sm">
        <Field label="Period start" value={sub.current_period_start ? new Date(sub.current_period_start).toLocaleDateString() : '—'} />
        <Field label="Period end" value={sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : '—'} />
        <Field label="Plan duration" value={sub.plan?.duration || '—'} />
        <Field label="Currency" value={sub.plan?.currency || '—'} />
        {sub.apple_product_id && (
          <Field label="Product" value={sub.apple_product_id} mono full />
        )}
        {sub.apple_original_transaction_id && (
          <Field label="Original txn" value={sub.apple_original_transaction_id} mono full />
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between bg-gray-50/60">
        <span className="text-[11px] text-gray-400">Record #{sub.id}</span>
        <a
          href={`${process.env.REACT_APP_STRAPI_API_URL || ''}/admin/content-manager/collectionType/api::user-subscription.user-subscription/${sub.documentId || sub.id}`}
          target="_blank" rel="noreferrer"
          className="text-[11px] font-semibold text-primary hover:underline">
          Edit in Strapi admin →
        </a>
      </div>
    </div>
  );
}

function Field({ label, value, mono, full }: { label: string; value: string; mono?: boolean; full?: boolean }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <dt className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">{label}</dt>
      <dd className={`mt-0.5 font-semibold text-gray-800 break-words ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}
